import React, { useEffect, useState } from "react";
import { api, isAuthed, logout, uploadFile, parseYouTubeId } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, ArrowUp, ArrowDown, Upload, Loader2, X, Film } from "lucide-react";

const SECTIONS = [
  { key: "site_config", label: "Site Config" },
  { key: "hero_videos", label: "Hero Videos" },
  { key: "home_circles", label: "Home Circles" },
  { key: "services", label: "Home Services (Cards)" },
  { key: "services_page", label: "Services Page" },
  { key: "services_videos", label: "Services Videos" },
  { key: "home_creatives", label: "Home Creatives" },
  { key: "client_logos", label: "Client Logos" },
  { key: "videos", label: "Home Videos" },
  { key: "creatives_segments", label: "Creatives Segments" },
  { key: "creatives_items", label: "Creatives Items" },
  { key: "subpage_blocks", label: "Subpage Blocks" },
  { key: "journey", label: "Journey (About)" },
  { key: "blogs", label: "Blogs" },
  { key: "faqs", label: "FAQs" },
  { key: "submissions", label: "Form Submissions" },
];

// Field schemas per collection
// Field types: text | textarea | number | json | image (upload) | video (upload) | select | checkbox
// `showIf: (draft) => bool` hides a field unless the condition holds.
const SCHEMAS = {
  hero_videos: [
    { k: "title", label: "Title (shown on the hero)", type: "text" },
    {
      k: "source",
      label: "Video source",
      type: "select",
      options: [
        { value: "youtube", label: "YouTube link" },
        { value: "upload", label: "Upload to our server (no YouTube)" },
      ],
      default: "youtube",
    },
    {
      k: "youtube_url",
      label: "YouTube link",
      type: "text",
      help: "Paste any YouTube link — youtube.com/watch?v=…, youtu.be/…, or /shorts/…",
      showIf: (d) => (d.source || "youtube") === "youtube",
    },
    {
      k: "video_url",
      label: "Video file",
      type: "video",
      help: "MP4 (H.264) recommended, ideally under 50 MB for fast loading. Max 500 MB.",
      showIf: (d) => d.source === "upload",
    },
    {
      k: "poster",
      label: "Poster image (optional)",
      type: "image",
      help: "Shown while the uploaded video loads.",
      showIf: (d) => d.source === "upload",
    },
    { k: "active", label: "Show on website", type: "checkbox", default: true },
    { k: "order", label: "Order", type: "number" },
  ],
  home_circles: [
    { k: "image", label: "Image", type: "image" },
    { k: "label", label: "Label", type: "text" },
    { k: "tag", label: "Tag", type: "text" },
    { k: "order", label: "Order", type: "number" },
  ],
  services: [
    { k: "title", label: "Title", type: "text" },
    { k: "body", label: "Body (legacy — not shown on hover-image cards)", type: "textarea" },
    { k: "image", label: "Card image", type: "image", help: "Upload from your computer. Shown by default; title appears on hover." },
    { k: "order", label: "Order", type: "number" },
  ],
  services_page: [
    { k: "title", label: "Title", type: "text" },
    { k: "body", label: "Body (revealed on hover via typewriter)", type: "textarea" },
    { k: "image", label: "Image", type: "image" },
    { k: "order", label: "Order", type: "number" },
  ],
  services_videos: [
    { k: "youtube_id", label: "YouTube ID", type: "text", help: "e.g. ScMzIvxBSi4" },
    { k: "title", label: "Title", type: "text" },
    { k: "order", label: "Order", type: "number" },
  ],
  home_creatives: [
    { k: "image", label: "Image", type: "image" },
    { k: "title", label: "Title", type: "text" },
    { k: "category", label: "Category key", type: "text", help: "social_media | videography | ad_campaigns | photography" },
    { k: "order", label: "Order", type: "number" },
  ],
  client_logos: [
    { k: "name", label: "Name", type: "text" },
    { k: "logo", label: "Logo", type: "image", help: "Upload a colour logo (PNG/SVG with transparent background looks best)." },
    { k: "order", label: "Order", type: "number" },
  ],
  videos: [
    { k: "youtube_id", label: "YouTube ID", type: "text", help: "e.g. ScMzIvxBSi4 (the part after watch?v=)" },
    { k: "title", label: "Title", type: "text" },
    { k: "order", label: "Order", type: "number" },
  ],
  creatives_segments: [
    { k: "key", label: "Category key", type: "text" },
    { k: "title", label: "Title", type: "text" },
    { k: "blurb", label: "Blurb", type: "textarea" },
    { k: "order", label: "Order", type: "number" },
  ],
  creatives_items: [
    { k: "category", label: "Category key", type: "text" },
    { k: "image", label: "Image", type: "image" },
    { k: "title", label: "Title", type: "text" },
    { k: "subtitle", label: "Subtitle", type: "text" },
    { k: "order", label: "Order", type: "number" },
  ],
  subpage_blocks: [
    { k: "category", label: "Category key", type: "text" },
    { k: "heading", label: "Heading", type: "text" },
    { k: "body", label: "Body", type: "textarea" },
  ],
  journey: [
    { k: "year", label: "Year", type: "text" },
    { k: "title", label: "Title", type: "text" },
    { k: "body", label: "Story", type: "textarea" },
    { k: "image", label: "Image", type: "image" },
    { k: "order", label: "Order", type: "number" },
  ],
  blogs: [
    { k: "title", label: "Title", type: "text" },
    { k: "slug", label: "Slug", type: "text" },
    { k: "excerpt", label: "Excerpt", type: "textarea" },
    { k: "cover", label: "Cover image", type: "image" },
    { k: "author", label: "Author", type: "text" },
    { k: "blocks", label: "Blocks JSON", type: "json", help: "Array of {type:'heading'|'paragraph'|'image', text/src}" },
  ],
  faqs: [
    { k: "page", label: "Page", type: "text", help: "home | services | creatives | about" },
    { k: "question", label: "Question", type: "text" },
    { k: "answer", label: "Answer", type: "textarea" },
    { k: "order", label: "Order", type: "number" },
  ],
};

// Extra processing before save, per collection
const TRANSFORMS = {
  hero_videos: (o) => {
    const out = { ...o, source: o.source || "youtube" };
    if (out.source === "youtube") {
      const id = parseYouTubeId(out.youtube_url);
      if (!id) throw new Error("Please paste a valid YouTube link");
      out.youtube_id = id;
    } else if (!out.video_url) {
      throw new Error("Please upload a video file");
    }
    out.active = out.active !== false;
    return out;
  },
};

// --- Upload widgets ---
const ImageUploadField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [showUrl, setShowUrl] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setBusy(true); setPct(0);
    try {
      const url = await uploadFile(file, setPct);
      onChange(url);
      toast.success("Image uploaded — click Create / Save to apply");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 flex gap-3 items-start">
      <div className="w-24 h-24 shrink-0 rounded-lg border border-ahm-teal/20 bg-ahm-muted/50 overflow-hidden flex items-center justify-center">
        {value ? <img src={value} alt="" className="w-full h-full object-contain" /> : <span className="text-[10px] text-ahm-teal/50">No image</span>}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer bg-ahm-teal text-ahm-cream hover:bg-ahm-orange transition ${busy ? "opacity-60 pointer-events-none" : ""}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? `Uploading ${pct}%` : value ? "Replace image" : "Upload image"}
            <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
          </label>
          {value && (
            <Button type="button" size="sm" variant="outline" onClick={() => onChange("")}>
              <X size={14} className="mr-1" /> Remove
            </Button>
          )}
        </div>
        <button type="button" className="text-[11px] text-ahm-teal/60 underline" onClick={() => setShowUrl((v) => !v)}>
          {showUrl ? "Hide link" : "or paste an image link"}
        </button>
        {showUrl && <Input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" />}
      </div>
    </div>
  );
};

const VideoUploadField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Please choose a video file");
    setBusy(true); setPct(0);
    try {
      const url = await uploadFile(file, setPct);
      onChange(url);
      toast.success("Video uploaded — click Create / Save to apply");
    } catch (err) {
      toast.error(err.response?.data?.detail || (err.response?.status === 413 ? "File too large for the server" : "Upload failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 space-y-2">
      {value ? (
        <video src={value} controls muted playsInline className="w-full max-w-md aspect-video rounded-lg bg-black" />
      ) : (
        <div className="w-full max-w-md aspect-video rounded-lg border border-dashed border-ahm-teal/30 bg-ahm-muted/40 flex flex-col items-center justify-center text-ahm-teal/50 text-xs gap-1">
          <Film size={22} /> No video uploaded
        </div>
      )}
      <div className="flex gap-2 flex-wrap items-center">
        <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer bg-ahm-teal text-ahm-cream hover:bg-ahm-orange transition ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? `Uploading ${pct}%` : value ? "Replace video" : "Upload video"}
          <input type="file" accept="video/mp4,video/webm,video/quicktime,video/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
        </label>
        {value && (
          <Button type="button" size="sm" variant="outline" onClick={() => onChange("")}>
            <X size={14} className="mr-1" /> Remove
          </Button>
        )}
      </div>
      {busy && (
        <div className="w-full max-w-md h-1.5 bg-ahm-teal/15 rounded-full overflow-hidden">
          <div className="h-full bg-ahm-orange transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

// --- Site Config editor (key/value) ---
const SiteConfigEditor = () => {
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/site_config").then((r) => setCfg(r.data || {}));
  useEffect(() => { load(); }, []);

  const save = async (k) => {
    setBusy(true);
    try {
      await api.put("/site_config", { key: k, value: cfg[k] });
      toast.success(`Saved ${k}`);
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const fields = [
    ["logo_url", "Logo"],
    ["company_name", "Company name"],
    ["tagline", "Tagline"],
    ["main_banner_text", "Main banner quote"],
    ["cta_banner_text", "CTA banner text"],
    ["about_intro_title", "About — Intro title"],
    ["about_intro_body", "About — Intro body (textarea)"],
    ["phone", "Phone"],
    ["email", "Email"],
    ["whatsapp_number", "WhatsApp number (digits only)"],
    ["address", "Address"],
    ["map_embed", "Google Maps embed URL"],
    ["footer_text", "Footer text"],
  ];

  return (
    <div className="space-y-6">
      {fields.map(([k, label]) => (
        <div key={k} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <label className="md:col-span-3 text-sm font-semibold text-ahm-teal pt-2">{label}</label>
          {k === "logo_url" ? (
            <div className="md:col-span-7">
              <ImageUploadField value={cfg[k] || ""} onChange={(v) => setCfg((c) => ({ ...c, [k]: v }))} testId={`cfg-${k}`} />
            </div>
          ) : k.endsWith("body") || k === "map_embed" ? (
            <Textarea
              data-testid={`cfg-${k}`}
              className="md:col-span-7 min-h-[80px]"
              value={cfg[k] || ""}
              onChange={(e) => setCfg((c) => ({ ...c, [k]: e.target.value }))}
            />
          ) : (
            <Input
              data-testid={`cfg-${k}`}
              className="md:col-span-7"
              value={cfg[k] || ""}
              onChange={(e) => setCfg((c) => ({ ...c, [k]: e.target.value }))}
            />
          )}
          <Button onClick={() => save(k)} disabled={busy} className="md:col-span-2 bg-ahm-teal hover:bg-ahm-orange" data-testid={`save-cfg-${k}`}>
            <Save size={14} className="mr-1" /> Save
          </Button>
        </div>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <label className="md:col-span-3 text-sm font-semibold text-ahm-teal pt-2">WhatsApp messages (one per line)</label>
        <Textarea
          data-testid="cfg-whatsapp_messages"
          className="md:col-span-7 min-h-[140px] font-mono text-sm"
          value={(cfg.whatsapp_messages || []).join("\n")}
          onChange={(e) => setCfg((c) => ({ ...c, whatsapp_messages: e.target.value.split("\n").filter(Boolean) }))}
        />
        <Button onClick={() => save("whatsapp_messages")} className="md:col-span-2 bg-ahm-teal hover:bg-ahm-orange" data-testid="save-cfg-whatsapp_messages">
          <Save size={14} className="mr-1" /> Save
        </Button>
      </div>
    </div>
  );
};

// --- Generic CRUD editor ---
const CrudEditor = ({ collection }) => {
  const schema = SCHEMAS[collection];
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});

  const load = () => api.get(`/${collection}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [collection]);

  const initDraft = () => {
    const d = {};
    schema.forEach((f) => {
      if (f.default !== undefined) d[f.k] = f.default;
      else if (f.type === "number") d[f.k] = items.length;
      else if (f.type === "json") d[f.k] = "[]";
      else if (f.type === "checkbox") d[f.k] = false;
      else d[f.k] = "";
    });
    setDraft(d);
  };
  useEffect(() => { initDraft(); }, [collection, items.length]);  // eslint-disable-line

  const prepare = (obj) => {
    const out = { ...obj };
    schema.forEach((f) => {
      if (f.type === "number" && out[f.k] !== "" && out[f.k] !== undefined) out[f.k] = Number(out[f.k]) || 0;
      if (f.type === "json") {
        try { out[f.k] = typeof out[f.k] === "string" ? JSON.parse(out[f.k] || "[]") : out[f.k]; }
        catch (e) { throw new Error(`Invalid JSON in field ${f.k}`); }
      }
    });
    return TRANSFORMS[collection] ? TRANSFORMS[collection](out) : out;
  };

  const create = async () => {
    try {
      const payload = prepare(draft);
      await api.post(`/${collection}`, payload);
      toast.success("Created");
      load();
    } catch (e) { toast.error(e.message || e.response?.data?.detail || "Failed"); }
  };

  const update = async (id, obj) => {
    try {
      const payload = prepare(obj);
      await api.put(`/${collection}/${id}`, payload);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e.message || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/${collection}/${id}`);
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
    load();
  };

  // Swap the `order` field of an item with its neighbour.
  const reorder = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[index];
    const b = items[target];
    const aOrder = typeof a.order === "number" ? a.order : index;
    const bOrder = typeof b.order === "number" ? b.order : target;
    const optimistic = [...items];
    optimistic[index] = { ...a, order: bOrder };
    optimistic[target] = { ...b, order: aOrder };
    optimistic.sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    setItems(optimistic);
    try {
      await Promise.all([
        api.put(`/${collection}/${a.id}`, { ...a, order: bOrder }),
        api.put(`/${collection}/${b.id}`, { ...b, order: aOrder }),
      ]);
      load();
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-ahm-muted/40 border border-ahm-teal/15 rounded-xl p-5">
        <h4 className="font-semibold text-ahm-teal text-lg mb-4 flex items-center gap-2"><Plus size={16} /> Add new</h4>
        <Fields schema={schema} value={draft} onChange={setDraft} testPrefix={`new-${collection}`} />
        <Button onClick={create} className="bg-ahm-orange hover:bg-ahm-teal mt-4" data-testid={`create-${collection}`}>
          Create
        </Button>
      </div>

      <div className="space-y-4">
        {items.map((it, idx) => (
          <ItemRow
            key={it.id}
            item={it}
            index={idx}
            total={items.length}
            schema={schema}
            onUpdate={(o) => update(it.id, o)}
            onDelete={() => del(it.id)}
            onMoveUp={() => reorder(idx, -1)}
            onMoveDown={() => reorder(idx, 1)}
            testPrefix={collection}
          />
        ))}
        {items.length === 0 && <p className="text-ahm-teal/60">No items yet.</p>}
      </div>
    </div>
  );
};

const Fields = ({ schema, value, onChange, testPrefix }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {schema.map((f) => {
      if (f.showIf && !f.showIf(value)) return null;
      const raw = value[f.k] ?? f.default;
      const v = f.type === "json" && typeof value[f.k] !== "string" ? JSON.stringify(value[f.k] || [], null, 2) : (raw ?? "");
      const set = (val) => onChange({ ...value, [f.k]: val });
      const tid = `${testPrefix}-${f.k}`;
      const wide = f.type === "image" || f.type === "video" || f.type === "textarea" || f.type === "json";
      let control;
      if (f.type === "image") {
        control = <ImageUploadField value={v} onChange={set} testId={tid} />;
      } else if (f.type === "video") {
        control = <VideoUploadField value={v} onChange={set} testId={tid} />;
      } else if (f.type === "select") {
        control = (
          <select
            data-testid={tid}
            value={v || f.options[0].value}
            onChange={(e) => set(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      } else if (f.type === "checkbox") {
        control = (
          <span className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              data-testid={tid}
              checked={raw !== false && raw !== "" && raw !== undefined}
              onChange={(e) => set(e.target.checked)}
              className="w-4 h-4 accent-ahm-teal"
            />
            <span className="text-sm text-ahm-teal/80">Yes</span>
          </span>
        );
      } else if (f.type === "textarea" || f.type === "json") {
        control = (
          <Textarea
            data-testid={tid}
            value={v}
            onChange={(e) => set(e.target.value)}
            className={`mt-1 min-h-[80px] ${f.type === "json" ? "font-mono text-xs" : ""}`}
          />
        );
      } else {
        control = (
          <Input
            data-testid={tid}
            type={f.type === "number" ? "number" : "text"}
            value={v}
            onChange={(e) => set(e.target.value)}
            className="mt-1"
          />
        );
      }
      return (
        <div key={f.k} className={`block ${wide ? "md:col-span-2" : ""}`}>
          <span className="text-xs uppercase tracking-wider text-ahm-teal/70 font-semibold">{f.label}</span>
          {control}
          {f.help && <p className="text-[11px] text-ahm-teal/55 mt-1">{f.help}</p>}
        </div>
      );
    })}
  </div>
);

const ItemRow = ({ item, index, total, schema, onUpdate, onDelete, onMoveUp, onMoveDown, testPrefix }) => {
  const [draft, setDraft] = useState(() => {
    const d = { ...item };
    schema.forEach((f) => { if (f.type === "json") d[f.k] = JSON.stringify(item[f.k] || [], null, 2); });
    return d;
  });
  return (
    <div className="border border-ahm-teal/15 rounded-xl p-5 bg-white">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <span className="font-mono text-[11px] text-ahm-teal/50">#{index + 1} · {item.id?.slice(0, 8)}</span>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={onMoveUp}
            disabled={index === 0}
            data-testid={`move-up-${testPrefix}-${item.id}`}
            title="Move up"
          >
            <ArrowUp size={14} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onMoveDown}
            disabled={index === total - 1}
            data-testid={`move-down-${testPrefix}-${item.id}`}
            title="Move down"
          >
            <ArrowDown size={14} />
          </Button>
          <Button size="sm" onClick={() => onUpdate(draft)} className="bg-ahm-teal hover:bg-ahm-orange" data-testid={`update-${testPrefix}-${item.id}`}>
            <Save size={12} className="mr-1" /> Save
          </Button>
          <Button size="sm" variant="destructive" onClick={onDelete} data-testid={`delete-${testPrefix}-${item.id}`}>
            <Trash2 size={12} />
          </Button>
        </div>
      </div>
      <Fields schema={schema} value={draft} onChange={setDraft} testPrefix={`edit-${testPrefix}-${item.id}`} />
    </div>
  );
};

const Submissions = ({ onCountChange }) => {
  const [items, setItems] = useState([]);
  const load = () =>
    api.get("/admin/contact_submissions").then((r) => {
      setItems(r.data);
      if (onCountChange) {
        const unread = (r.data || []).filter((x) => !x.read).length;
        onCountChange(unread);
      }
    });

  // Initial load + poll every 15s so newly-submitted entries appear automatically.
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, []);  // eslint-disable-line

  const del = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/contact_submissions/${id}`);
    load();
  };
  const markRead = async (id) => {
    await api.put(`/admin/contact_submissions/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.put(`/admin/contact_submissions/mark_all_read`);
    load();
  };

  const unread = items.filter((x) => !x.read).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <p className="text-sm text-ahm-teal/75">
          {items.length} total · <span className="text-ahm-orange font-semibold">{unread} unread</span>
        </p>
        {unread > 0 && (
          <Button variant="outline" onClick={markAll} data-testid="mark-all-read">
            Mark all as read
          </Button>
        )}
      </div>
      {items.length === 0 && <p className="text-ahm-teal/60">No submissions yet. Form responses will appear here in real time.</p>}
      {items.map((s) => (
        <div
          key={s.id}
          className={`border rounded-xl p-5 bg-white transition-colors ${
            s.read ? "border-ahm-teal/15" : "border-ahm-orange/45 bg-ahm-yellow/10"
          }`}
          data-testid={`submission-${s.id}`}
          data-read={s.read ? "1" : "0"}
        >
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-3 flex-wrap">
                {!s.read && (
                  <span
                    className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] font-bold bg-ahm-orange text-ahm-cream px-2 py-1 rounded-full"
                    data-testid="submission-new-badge"
                  >
                    New
                  </span>
                )}
                <h4 className="font-display text-2xl text-ahm-teal">{s.name}</h4>
                <span className="text-sm text-ahm-teal/65">{s.email} · {s.phone}</span>
              </div>
              <p className="mt-1 text-sm text-ahm-teal/60">{s.who} — {s.purpose} — heard from {s.heardFrom}</p>
              <p className="mt-3 text-base text-ahm-teal/85 whitespace-pre-wrap">{s.message}</p>
              <p className="mt-3 font-mono text-[11px] text-ahm-teal/50">{s.created_at}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {!s.read && (
                <Button size="sm" variant="outline" onClick={() => markRead(s.id)} data-testid={`mark-read-${s.id}`}>
                  Mark read
                </Button>
              )}
              <Button size="sm" variant="destructive" onClick={() => del(s.id)} data-testid={`delete-submission-${s.id}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!isAuthed()) nav("/admin/login");
  }, [nav]);

  // Poll the unread count every 20s so the badge stays fresh.
  useEffect(() => {
    if (!isAuthed()) return;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/contact_submissions/unread_count");
        setUnread(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 20000);
    return () => clearInterval(t);
  }, []);

  const onLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-ahm-cream" data-testid="admin-dashboard">
      <header className="border-b border-ahm-teal/15 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-display text-2xl text-ahm-teal">AHM · Admin</Link>
            {unread > 0 && (
              <button
                onClick={() => setTab("submissions")}
                data-testid="header-unread-pill"
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] bg-ahm-orange text-ahm-cream rounded-full px-3 py-1 hover:bg-ahm-teal transition"
              >
                <span className="w-2 h-2 rounded-full bg-ahm-yellow animate-pulse" />
                {unread} new {unread === 1 ? "lead" : "leads"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-ahm-teal/70 hover:text-ahm-orange uppercase tracking-[0.2em]">View site →</Link>
            <Button variant="outline" onClick={onLogout} data-testid="admin-logout-btn">Log out</Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-5xl md:text-6xl text-ahm-teal mb-8">Content Studio</h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto justify-start bg-ahm-muted/60 p-1 gap-1">
            {SECTIONS.map((s) => (
              <TabsTrigger
                key={s.key}
                value={s.key}
                data-testid={`tab-${s.key}`}
                className="data-[state=active]:bg-ahm-teal data-[state=active]:text-ahm-cream relative"
              >
                {s.label}
                {s.key === "submissions" && unread > 0 && (
                  <span
                    className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-ahm-orange text-ahm-cream"
                    data-testid="submissions-tab-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="mt-8">
            <TabsContent value="site_config"><SiteConfigEditor /></TabsContent>
            <TabsContent value="submissions"><Submissions onCountChange={setUnread} /></TabsContent>
            {Object.keys(SCHEMAS).map((k) => (
              <TabsContent key={k} value={k}><CrudEditor collection={k} /></TabsContent>
            ))}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
